import React from 'react';
import styled, { css } from 'styled-components';

import { Block, Heading, Text } from '../typography';
import { Cash } from '../../assets/icons';
import { formatCashOutValue } from '../../modules/bet/cash-out/cash-out';
import { RewardsNotAvailableLabel } from '../../modules/bet/cash-out';
import { CashOutAvailabilityReason, BetStatusType, PlayerCardOperator } from '../../types';
import { formatMoneyWithSymbol } from '../../lib/money-formatter';
import { zeroMoney } from '../../lib/money';
import { getRewardTerminologyForPlayerCardOperator } from '../../betting/bet-with-credits';
import { InjuryProtection as InjuryProtectionIcon } from '../../assets/icons/';
import { BetDetailsVariant } from '.';

export type CashOutLabelDetails = {
  canCashOut: boolean;
  cashOutAvailabilityReason: CashOutAvailabilityReason | null;
  cashOutValue?: number;
  currency: string;
};

export type BetTotalsProps = {
  betStatus: BetStatusType;
  stake: string;
  winAmount?: string;
  toWin?: string;
  originalToWin?: string;
  potentialReturns: string;
  settled: boolean;
  withheldAmount?: string;
  isParimutuel: boolean;
  isCashedOut: boolean;
  variant?: BetDetailsVariant;
  showInjuryProtectionMessage: boolean;
  showVoidedAdjustmentMessage: boolean;
  cashOutLabelDetails?: CashOutLabelDetails;
  settledForLess: boolean;
  rewardCreditsPerLine: number | null;
  playerCardOperator?: PlayerCardOperator;
};

const BetTotals = ({
  betStatus,
  stake,
  winAmount,
  potentialReturns,
  originalToWin,
  settled,
  withheldAmount,
  isParimutuel,
  isCashedOut,
  variant = 'default',
  cashOutLabelDetails,
  settledForLess,
  rewardCreditsPerLine,
  playerCardOperator,
  showInjuryProtectionMessage,
  showVoidedAdjustmentMessage
}: BetTotalsProps) => {
  const cashOutLabel = cashOutLabelDetails && getCashOutLabel(cashOutLabelDetails);
  const autoPayout = [BetStatusType.VoidCredits, BetStatusType.PushCredits].includes(betStatus);
  const { unit: rewardUnit } = getRewardTerminologyForPlayerCardOperator(playerCardOperator);
  const [stakeLabel, stakeCost] = rewardCreditsPerLine
    ? [`Reward ${rewardUnit} Applied:`, `${rewardCreditsPerLine.toLocaleString()} | ${stake}`]
    : ['Ticket Cost:', stake];

  return (
    <Wrapper as="div" size="lg" variant={variant}>
      {cashOutLabel && (
        <CashOutLabel as="div" weight={900} size="xl" data-testid="cash-out-label">
          {cashOutLabel}
        </CashOutLabel>
      )}

      <VoidOrInjuryProtectionMessage
        showInjuryProtectionMessage={showInjuryProtectionMessage}
        showVoidedAdjustmentMessage={showVoidedAdjustmentMessage}
      />

      <Totals>
        <Stake>
          <BetTotalsBlockDL>
            <BetTotalsBlockDT>{stakeLabel}</BetTotalsBlockDT>
            <BetTotalsBlockDD className="qa-bet-finder-total-stake">{stakeCost}</BetTotalsBlockDD>
          </BetTotalsBlockDL>
          {autoPayout && (
            <CreditsRefund as="p" size="sm" weight="regular">
              These Reward {rewardUnit} will be returned to your account automatically
            </CreditsRefund>
          )}
        </Stake>
        {!isParimutuel && (
          <BetTotalsBlockDL>
            <BetTotalsBlockDT>Win Amount:</BetTotalsBlockDT>
            <BetTotalsBlockDD className="qa-bet-finder-total-win">
              {originalToWin && <OriginalAmountText>{originalToWin}</OriginalAmountText>}
              <Text>{settledForLess || autoPayout ? '--' : winAmount}</Text>
            </BetTotalsBlockDD>
          </BetTotalsBlockDL>
        )}
        {withheldAmount && (
          <BetTotalsBlockDL>
            <BetTotalsBlockDT>Tax Withheld:</BetTotalsBlockDT>
            <BetTotalsBlockDD className="qa-bet-finder-total-withheld">{withheldAmount}</BetTotalsBlockDD>
          </BetTotalsBlockDL>
        )}
        <BetTotalsBlockDL>
          <BetTotalsBlockDT>{getReturnsLabel({ settled, isCashedOut })}</BetTotalsBlockDT>
          <BetTotalsBlockDD className="qa-bet-finder-total-returns">
            <HighlightReturns highlight={isCashedOut}>
              {betStatus === BetStatusType.VoidCredits ? formatMoneyWithSymbol(zeroMoney(), '$') : potentialReturns}
            </HighlightReturns>
          </BetTotalsBlockDD>
        </BetTotalsBlockDL>
      </Totals>
    </Wrapper>
  );
};

function getReturnsLabel({ settled, isCashedOut }: Pick<BetTotalsProps, 'isCashedOut' | 'settled'>) {
  if (settled && !isCashedOut) {
    return 'Total Payout:';
  }
  if (!settled) {
    return 'Max Payout:';
  }
  return (
    <CashOutPayout weight="bold">
      <Cash /> Early Cashout Payout:
    </CashOutPayout>
  );
}

function getCashOutLabel({
  canCashOut,
  cashOutValue,
  currency,
  cashOutAvailabilityReason
}: {
  currency: string;
  cashOutValue?: number;
  canCashOut: boolean;
  cashOutAvailabilityReason: CashOutAvailabilityReason | null;
}) {
  if (!cashOutAvailabilityReason && (!canCashOut || cashOutValue == undefined)) {
    return null;
  }
  if (['ANONYMOUS_BET', 'UNSUPPORTED_REWARDS'].some(reason => reason === cashOutAvailabilityReason)) {
    return <RewardsNotAvailableLabel />;
  }
  if (cashOutAvailabilityReason === 'NOT_AVAILABLE_FOR_BET' || cashOutValue == undefined) {
    return 'Cashout Currently Unavailable';
  }
  return `Cashout: ${formatCashOutValue({ cashOutValue, currency })}`;
}

function VoidOrInjuryProtectionMessage({
  showVoidedAdjustmentMessage,
  showInjuryProtectionMessage
}: {
  showVoidedAdjustmentMessage: boolean;
  showInjuryProtectionMessage: boolean;
}) {
  if (!showInjuryProtectionMessage && !showVoidedAdjustmentMessage) return null;

  return (
    <>
      {showInjuryProtectionMessage ? (
        <Message variant="injuryProtection">
          <InjuryProtectionIcon style={{ flexShrink: 0 }} />
          This bet is covered by Injury Protection.
        </Message>
      ) : (
        <Message variant="alert">
          One or more of the legs were voided. The odds and payout have been adjusted to reflect this change.
        </Message>
      )}
    </>
  );
}

const Wrapper = styled(Heading)<{ variant?: BetDetailsVariant }>`
  background-color: ${({ theme }) => theme.colors.white};
  border-top: 1px solid ${({ theme }) => theme.colors.supporting.accent300};
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 2.4rem;
  padding: ${({ variant }) => (variant === 'compact' ? '1rem' : '2.4rem')};
`;

const Message = styled(Block).attrs({
  size: 'md',
  weight: 'regular'
})<{ variant: 'alert' | 'injuryProtection' }>`
  align-items: center;
  background: ${({ theme, variant }) => theme.colors.status[variant]};
  display: flex;
  flex-shrink: 0;
  gap: 0.6rem;
  max-width: 33rem;
  padding: 1.2rem 1.6rem;
  border-radius: 0.4rem;
  ${({ theme, variant }) =>
    variant === 'injuryProtection' &&
    css`
      color: ${theme.colors.white};
      background: ${theme.components.injuryProtection.gradient};
    `}
`;

const OriginalAmountText = styled(Text)`
  text-decoration: line-through ${({ theme }) => theme.colors.red};
  color: ${({ theme }) => theme.colors.neutral};
`;

const Totals = styled.div`
  display: flex;
  flex-direction: column;
`;

const BetTotalsBlockDL = styled.dl<{ marginTop?: string }>`
  display: flex;
  justify-content: flex-end;
  margin: 0;
`;

const BetTotalsBlockDT = styled(Text).attrs(() => ({ as: 'dt', weight: 600 }))`
  font-style: normal;
  min-width: 12rem;
  text-align: right;
`;

const BetTotalsBlockDD = styled.dd`
  margin-left: 0.6rem;
  min-width: 20rem;
  text-align: right;
  > * + * {
    margin-left: 1.1rem;
  }
`;

const Stake = styled.div<{ marginTop?: string }>`
  align-self: flex-end;
  margin: 0;
`;

const CreditsRefund = styled(Text)`
  color: ${({ theme }) => theme.colors.status.error};
  margin: 0;
  text-align: right;
`;

const CashOutPayout = styled(Block)`
  display: flex;
  align-items: center;
  gap: 0.7rem;
  color: ${({ theme }) => theme.colors.price};
`;

const CashOutLabel = styled(Heading)`
  background: ${({ theme }) => theme.colors.price};
  color: ${({ theme }) => theme.colors.white};
  margin-right: auto;
  padding: 1.2rem 2rem;
`;

const HighlightReturns = styled.span<{ highlight: boolean }>`
  ${({ theme, highlight }) =>
    highlight &&
    css`
      color: ${theme.colors.price};
    `};
`;

export default BetTotals;
